import { inspectionAreasKey, inspectionSheetKey } from '@/hooks/keyUtils'
import { toaster } from '@/system/toaster'
import { useAuth0 } from '@auth0/auth0-react'
import { useTranslation } from '@infraloop/i18n'
import {
  CreateInspectionAreaWithFilesParams,
  FileProcessingStatus,
  InspectionArea,
  InspectionSheet,
  UpdateInspectionAreaWithFilesParams,
  createInspectionAreaWithFiles,
  deleteInspectionArea,
  getErrorMesssage,
  getInspectionAreas,
  updateInspectionAreaWithFiles,
} from '@infraloop/services'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

/** How often to re-check the areas while a point cloud is being converted (ms). */
const PROCESSING_POLL_INTERVAL = 5 * 1000

/**
 * Loads the inspection areas for a project — the expanded sub-table rows — and owns the
 * area mutations. The query is gated on `enabled` so a project's areas are only fetched
 * once its row is expanded. While any area's point cloud is still converting, the list
 * is polled so the sub-table's processing status stays live (matching the legacy
 * dashboard).
 *
 * @param projectId Project whose inspection areas to load.
 * @param enabled Whether the project row is expanded (drives lazy fetching).
 */
const useInspectionAreas = (projectId: string, enabled: boolean) => {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const {
    data: areas = [],
    isLoading,
    isFetching,
  } = useQuery<InspectionArea[]>({
    queryKey: inspectionAreasKey(projectId),
    enabled: enabled && Boolean(projectId),
    staleTime: 60 * 1000,
    refetchInterval: (query) =>
      query.state.data?.some((area) => area.downsample_status?.status === FileProcessingStatus.Running)
        ? PROCESSING_POLL_INTERVAL
        : false,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      return getInspectionAreas(accessToken, projectId)
    },
  })

  /**
   * Create an inspection area with its point-cloud (and optional mesh) upload, then
   * add it to the project's sub-table.
   *
   * Creating is two-phase server-side (the area record is created first, its point cloud
   * is attached after the upload), so for the length of the upload the backend already
   * lists the new area *without* its file. Any fetch that lands in that window — the
   * processing poll above, a window-focus refetch — puts that fileless row in the cache,
   * which is why this upserts by id instead of appending (an append left the area listed
   * twice, the second row reading 点群なし) and cancels in-flight fetches first (a reply
   * that read the fileless row would otherwise overwrite this write).
   */
  const createAreaMutation = useMutation({
    mutationFn: async (params: CreateInspectionAreaWithFilesParams) => {
      const accessToken = await getAccessTokenSilently()
      return createInspectionAreaWithFiles(accessToken, projectId, params)
    },
    onSuccess: async (created) => {
      await queryClient.cancelQueries({ queryKey: inspectionAreasKey(projectId) })
      queryClient.setQueryData<InspectionArea[]>(inspectionAreasKey(projectId), (prev = []) =>
        prev.some((a) => a.inspection_area_id === created.inspection_area_id)
          ? prev.map((a) => (a.inspection_area_id === created.inspection_area_id ? created : a))
          : [...prev, created],
      )
      toaster.success({ description: t('hatsuly.toast.area.created') })
    },
    onError: (error) => {
      toaster.error({ description: getErrorMesssage(error, t) })
    },
  })

  /**
   * Update an inspection area (name, inspection-sheet metadata, and optional replacement
   * file uploads), refreshing it in the sub-table and its cached sheet.
   */
  const updateAreaMutation = useMutation({
    mutationFn: async (params: UpdateInspectionAreaWithFilesParams) => {
      const accessToken = await getAccessTokenSilently()
      return updateInspectionAreaWithFiles(accessToken, projectId, params)
    },
    onSuccess: (updated, { inspectionAreaId, sheet }) => {
      queryClient.setQueryData<InspectionArea[]>(inspectionAreasKey(projectId), (prev = []) =>
        prev.map((a) => (a.inspection_area_id === updated.inspection_area_id ? updated : a)),
      )
      if (sheet) {
        queryClient.setQueryData<InspectionSheet | null>(inspectionSheetKey(projectId, inspectionAreaId), sheet)
      }
      toaster.success({ description: t('hatsuly.toast.area.updated') })
    },
    onError: (error) => {
      toaster.error({ description: getErrorMesssage(error, t) })
    },
  })

  /**
   * Delete an inspection area and drop it from the project's sub-table.
   */
  const deleteAreaMutation = useMutation({
    mutationFn: async ({ inspectionAreaId }: { inspectionAreaId: string }) => {
      const accessToken = await getAccessTokenSilently()
      await deleteInspectionArea(accessToken, projectId, inspectionAreaId)
      return inspectionAreaId
    },
    onSuccess: (inspectionAreaId) => {
      queryClient.setQueryData<InspectionArea[]>(inspectionAreasKey(projectId), (prev = []) =>
        prev.filter((a) => a.inspection_area_id !== inspectionAreaId),
      )
      toaster.success({ description: t('hatsuly.toast.area.deleted') })
    },
    onError: (error) => {
      toaster.error({ description: getErrorMesssage(error, t) })
    },
  })

  return {
    areas,
    isLoadingAreas: isLoading,
    isFetchingAreas: isFetching,

    createArea: createAreaMutation.mutateAsync,
    isCreatingArea: createAreaMutation.isPending,

    updateArea: updateAreaMutation.mutateAsync,
    isUpdatingArea: updateAreaMutation.isPending,

    deleteArea: deleteAreaMutation.mutateAsync,
    isDeletingArea: deleteAreaMutation.isPending,
  }
}

export default useInspectionAreas
